import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { Modal, message } from "antd";
import moment from "moment";
import { LoadingStatusCode } from "../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../lib/utils/catchApiError";
import {
  fetchOfferListRoute,
  fetchServiceTypeListRoute,
  createOfferRoute,
  fetchOfferByIdRoute,
  fetchOfferVersionsByIdRoute,
  fetchServiceProfileVersionsRoute,
  updateOfferRoute,
  closeOfferVersionRoute,
  fetchOfferServiceProfileByIdRoute,
  updateOfferServiceProfileRoute,
  fetchOfferHierarchyRoute,
  createOfferHierarchyRoute,
  fetchCurrentOfferHierarchyVersionsRoute,
  closeOfferHierarchyRoute,
  deleteOfferHierarchyRoute,
  createOfferPriceRoute,
  fetchOfferPriceRoute,
  fetchServiceRateVersionsRoute,
  editOfferPriceRoute,
  closeOfferPriceRoute,
  deleteOfferPriceRoute,
  fetchServiceRatesVersionsRoute,
} from "../../../lib/routes/offering";
import { IServiceProfile, Offer, ServiceRate, ServiceType } from "../../../lib/types";
import {
  IServiceTypeSearch,
  IOfferListSearch,
  IOfferHierarchy,
  IBodyCreateHierarchy,
} from "./types";
import { sortByToDate } from "../../../lib/utils";
import {
  IOfferPrice,
  IOfferPriceCloseParams,
  IOfferPriceCreateParams,
  IOfferPriceEditParams,
} from "../../../lib/types/OfferPrice";
import { baseUrl } from "../../../lib/routes/baseUrl";

interface InitialState {
  offerList: Offer[];
  offerListTotalElements: number;
  offerListLoading: LoadingStatusCode;
  serviceTypeList: ServiceType[];
  serviceTypeListLoading: LoadingStatusCode;
  offer?: Offer;
  offerLoading: LoadingStatusCode;
  serviceProfile?: IServiceProfile;
  serviceProfileLoading: LoadingStatusCode;
  offerVersions: Offer[];
  offerVersionsLoading: LoadingStatusCode;
  offerVersionsNotForViewing: Offer[];
  offerVersionsNotForViewingLoading: LoadingStatusCode;
  serviceRates: ServiceRate<string>[];
  serviceRatesLoading: LoadingStatusCode;
  serviceRateVersions: ServiceRate<string>[];
  serviceRateVersionsLoading: LoadingStatusCode;
  serviceProfileVersions: IServiceProfile[];
  serviceProfileVersionsLoading: LoadingStatusCode;
  offerHierarchy?: IOfferHierarchy;
  offerHierarchyLoading: LoadingStatusCode;
  offerHierarchyVersions?: IOfferHierarchy;
  offerHierarchyVersionsLoading: LoadingStatusCode;
  hierarchyOfferList: Offer[];
  hierarchyOfferListLoading: LoadingStatusCode;
  offerPrice?: IOfferPrice;
  offerPriceLoading: LoadingStatusCode;
  defaultServiceNomenclature: any[];
  defaultServiceNomenclatureLoading: LoadingStatusCode;
  editOfferPriceLoading: LoadingStatusCode;
  closeOfferPriceLoading: LoadingStatusCode;
  createOfferPriceLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  offerList: [],
  offerListTotalElements: 0,
  offerListLoading: "idle",
  serviceTypeList: [],
  serviceTypeListLoading: "idle",
  offerLoading: "idle",
  serviceProfileLoading: "idle",
  offerVersions: [],
  offerVersionsLoading: "idle",
  offerVersionsNotForViewing: [],
  offerVersionsNotForViewingLoading: "idle",
  serviceProfileVersions: [],
  serviceProfileVersionsLoading: "idle",
  offerHierarchyLoading: "idle",
  offerHierarchyVersionsLoading: "idle",
  hierarchyOfferList: [],
  hierarchyOfferListLoading: "idle",
  offerPriceLoading: "idle",
  defaultServiceNomenclature: [],
  defaultServiceNomenclatureLoading: "idle",
  serviceRateVersions: [],
  serviceRateVersionsLoading: "idle",
  serviceRates: [],
  serviceRatesLoading: "idle",
  editOfferPriceLoading: "idle",
  closeOfferPriceLoading: "idle",
  createOfferPriceLoading: "idle",
};

// Получение списка предложений
export const fetchOfferList = createAsyncThunk(
  "offering/fetchOfferList",
  async (searchParams: Partial<IOfferListSearch<string>>) => {
    try {
      const response = axios.get(fetchOfferListRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка предложений. Попробуйте позже",
      });
    }
  }
);

// Получение списка типов продуктов
export const fetchServiceTypeList = createAsyncThunk(
  "offering/fetchServiceTypeList",
  async (searchParams: IServiceTypeSearch) => {
    try {
      const response = axios.get(fetchServiceTypeListRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов продуктов. Попробуйте позже",
      });
    }
  }
);

// Создание предложения без тарифа
export const createOffer = createAsyncThunk<Offer, Offer<string, "createObj">>(
  "offering/createOffer",
  async (formData) => {
    try {
      const response = axios.post<Offer>(createOfferRoute(), formData);
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания предложения. Попробуйте позже",
      });
    }
  }
);

// Обновление предложения
export const updateOfferVersion = createAsyncThunk<
  Promise<any>,
  { offer: Offer; force: boolean; reloadOfferFromDate: string }
>("offering/updateOfferVersion", async (params, { dispatch }) => {
  const { offer, force, reloadOfferFromDate } = params;
  try {
    const routeParams = {
      id: offer.id || 0,
      fromDate: offer.fromDate,
      force,
    };
    await axios.patch<Offer>(updateOfferRoute(routeParams), offer);
    Modal.success({
      title: `Версия предложения ${force ? "обновлена" : "создана"}`,
    });
    return Promise.all([
      dispatch(
        fetchOfferByIdFromVersions({
          id: offer.id!,
          onDate: reloadOfferFromDate,
          allObjectsVersions: true,
          withChars: true,
        })
      ),
      dispatch(
        fetchOfferVersionsByIdWithParams({
          id: offer.id!,
          searchParams: {
            allObjectsVersions: true,
            withChars: true,
          },
        })
      ),
    ]);
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка обновления предложения. Попробуйте позже",
    });
  }
});

// Закрытие версии предложения
export const closeOfferVersion = createAsyncThunk<Offer, { id: number; fromDate: string }>(
  "closeOfferVersion/closeOfferVersion",
  async (params, { dispatch }) => {
    try {
      const response = axios.delete<Offer>(closeOfferVersionRoute(params));
      const { data } = await response;
      Modal.success({
        title: "Период действия предложения изменен",
      });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка закрытия версии предложения. Попробуйте позже",
      });
    }
  }
);

// Получение предложения по id
// Для стендов с продуктами НЕ ИСПОЛЬЗОВАТЬ!!! Использовать: fetchOfferByIdFromVersions
const fetchOfferById = createAsyncThunk<Offer, { id: number; onDate: string }>(
  "offering/fetchOfferById",
  async (params) => {
    try {
      const response = axios.get<Offer>(fetchOfferByIdRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки предложения. Попробуйте позже",
      });
    }
  }
);

// Получение предложения по id из метода получения версий, включающий заданную дату
// Пример использования: получить актуальную версию предложения
export const fetchOfferByIdFromVersions = createAsyncThunk<
  Offer,
  { id: number; onDate: string; allObjectsVersions?: boolean; withChars?: boolean }
>("offering/fetchOfferByIdFromVersions", async ({ id, onDate, ...searchParams }) => {
  try {
    const { data } = await axios.get<Offer[]>(fetchOfferVersionsByIdRoute(id, searchParams));
    const datesSatisfyingVersion = data?.find((version) => {
      const isSatisfying = moment(onDate).isBetween(version.fromDate, version.toDate, "day", "[]");
      return isSatisfying;
    });
    if (datesSatisfyingVersion) {
      return datesSatisfyingVersion;
    }
    return data[0];
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки предложения. Попробуйте позже",
    });
  }
});

// Получение версий предложения по id
// Для стендов с продуктами НЕ ИСПОЛЬЗОВАТЬ!!! Использовать: fetchOfferVersionsByIdWithParams
export const fetchOfferVersionsById = createAsyncThunk(
  "offering/fetchOfferVersionsById",
  async (id: number) => {
    try {
      const response = axios.get(fetchOfferVersionsByIdRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий предложения. Попробуйте позже",
      });
    }
  }
);

// Получение версий предложения по id (для служебного использования, не для просмотра)
export const fetchOfferVersionsNotForViewing = createAsyncThunk(
  "offering/fetchOfferVersionsNotForViewing",
  async (id: number) => {
    try {
      const response = axios.get(fetchOfferVersionsByIdRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий предложения. Попробуйте позже",
      });
    }
  }
);

// Получение версий предложения по id
export const fetchOfferVersionsByIdWithParams = createAsyncThunk(
  "offering/fetchOfferVersionsByIdWithParams",
  async ({
    id,
    searchParams,
  }: {
    id: number;
    searchParams: {
      allObjectsVersions: boolean;
      withChars: boolean;
    };
  }) => {
    try {
      const response = axios.get(fetchOfferVersionsByIdRoute(id, searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий предложения. Попробуйте позже",
      });
    }
  }
);

// Получение профиля оплаты по id
export const fetchOfferServiceProfileById = createAsyncThunk<
  IServiceProfile,
  { id: number; onDate?: string }
>("offering/fetchOfferServiceProfileById", async ({ id, onDate }) => {
  try {
    const response = axios.get<IServiceProfile>(fetchOfferServiceProfileByIdRoute(id, onDate));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки предложения. Попробуйте позже",
    });
  }
});

// Получение версий профиля оплаты
export const fetchServiceProfileVersions = createAsyncThunk<IServiceProfile[], number>(
  "offering/fetchServiceProfileVersions",
  async (id) => {
    try {
      const response = axios.get<IServiceProfile[]>(fetchServiceProfileVersionsRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий предложения. Попробуйте позже",
      });
    }
  }
);

// Обновление профиля оплаты предложения
export const updateOfferServiceProfileVersion = createAsyncThunk<
  Promise<any>,
  {
    serviceProfile: Partial<IServiceProfile>;
    force?: boolean;
  }
>("offering/updateOfferServiceProfileVersion", async (params, { dispatch }) => {
  const { serviceProfile, force } = params;
  try {
    await axios.patch<IServiceProfile>(
      updateOfferServiceProfileRoute({
        id: serviceProfile.id!,
        fromDate: serviceProfile.fromDate!,
        force,
      }),
      serviceProfile
    );
    Modal.success({
      title: `Версия профиля ${force ? "обновлена" : "создана"}`,
    });
    return Promise.all([
      dispatch(
        fetchOfferServiceProfileById({
          id: serviceProfile.id!,
          onDate: "",
        })
      ),
      dispatch(fetchServiceProfileVersions(serviceProfile.id!)),
    ]);
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка обновления версии профиля оплаты предложения. Попробуйте позже",
    });
  }
});

// Получение иерархии предложения
export const fetchOfferHierarchy = createAsyncThunk<IOfferHierarchy, number>(
  "offering/fetchOfferHierarchy",
  async (id) => {
    try {
      const response = axios.get<IOfferHierarchy>(fetchOfferHierarchyRoute({ id }));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки иерархии предложения. Попробуйте позже",
      });
    }
  }
);

// Получение иерархии предложения
export const fetchCurrentOfferHierarchy = createAsyncThunk<
  IOfferHierarchy,
  { id: number; onDate?: string }
>("offering/fetchCurrentOfferHierarchy", async ({ id, onDate }) => {
  try {
    const response = axios.get<IOfferHierarchy>(fetchOfferHierarchyRoute({ id, onDate }));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки иерархии предложения. Попробуйте позже",
    });
  }
});

// Создание иерархии предложения
export const createOfferHierarchy = createAsyncThunk<
  IOfferHierarchy[],
  { id: number; body: IBodyCreateHierarchy }
>("offering/createOfferHierarchy", async ({ id, body }) => {
  try {
    const response = axios.post<IOfferHierarchy[]>(createOfferHierarchyRoute(id), body);
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка",
    });
  }
});

// Закрытие версии иерархии предложения
export const closeOfferHierarchy = createAsyncThunk<
  void,
  { offeringLinkIds: number[]; fromDate: string }
>("offering/closeOfferHierarchy", async (params) => {
  try {
    const response = axios.delete(closeOfferHierarchyRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка закрытия версии иерархии предложения. Попробуйте позже",
    });
  }
});

// Удаление версии иерархии предложения
export const deleteOfferHierarchy = createAsyncThunk<void, number[]>(
  "offering/deleteOfferHierarchy",
  async (params) => {
    try {
      const response = axios.delete(deleteOfferHierarchyRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка удаления версии иерархии предложения. Попробуйте позже",
      });
    }
  }
);

// Просмотр версий предложения
export const fetchCurrentOfferHierarchyVersions = createAsyncThunk<IOfferHierarchy[], number>(
  "offering/fetchCurrentOfferHierarchyVersions",
  async (id) => {
    try {
      const response = axios.get<IOfferHierarchy[]>(fetchCurrentOfferHierarchyVersionsRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки истории иерархии предложения. Попробуйте позже",
      });
    }
  }
);

// Получение списка предложений, для иерархии (корневое и все вложенные предложения иерархии)
export const fetchHierarchyOfferList = createAsyncThunk<
  { content: Offer[]; dataForOfferId?: { isAddOffer: boolean; selectedOfferId: number } },
  Partial<
    IOfferListSearch<string> & { dataForOfferId?: { isAddOffer: boolean; selectedOfferId: number } }
  >
>("offering/fetchHierarchyOfferList", async (searchParams) => {
  try {
    // isAddOffer для случаев когда запрашиваемый офер один и не требуется доп запроса
    const { dataForOfferId } = searchParams;
    const copyParams = { ...searchParams };
    delete copyParams.dataForOfferId;
    const response = axios.get<{ content: Offer[] }>(fetchOfferListRoute(copyParams));
    const { data } = await response;
    return { content: data.content, dataForOfferId };
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки списка предложений для иерархии. Попробуйте позже",
    });
  }
});

// Создание цены для offer-a
export const createOfferPrice = createAsyncThunk<IOfferPrice, IOfferPriceCreateParams>(
  "offering/createOfferPrice",
  async (params) => {
    try {
      message.loading("Создание цены");
      const response = axios.post<IOfferPrice>(createOfferPriceRoute(), params);
      const { data } = await response;
      Modal.success({
        title: "Цена создана",
      });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания цены. Попробуйте позже",
      });
    } finally {
      message.destroy();
    }
  }
);

// Редактирование или добавление версии цены для offer-a
export const editOfferPrice = createAsyncThunk<IOfferPrice, IOfferPriceEditParams>(
  "offering/editOfferPrice",
  async ({ rateId, fromDate, force, params }) => {
    try {
      message.loading(force ? "Изменение цены" : "Создание версии цены");
      const response = axios.patch<IOfferPrice>(
        editOfferPriceRoute(rateId, fromDate, force),
        params
      );
      const { data } = await response;
      Modal.success({
        title: force ? "Цена обновлена" : "Новая версия цены создана",
      });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: force
          ? "Ошибка изменения цены. Попробуйте позже"
          : "Ошибка создания версии цены. Попробуйте позже",
      });
    } finally {
      message.destroy();
    }
  }
);

// Редактирование даты закрытия цены для offer-a
export const closeOfferPrice = createAsyncThunk<IOfferPrice, IOfferPriceCloseParams>(
  "offering/closeOfferPrice",
  async (params) => {
    try {
      message.loading("Изменение даты закрытия цены");
      const response = axios.delete<IOfferPrice>(closeOfferPriceRoute(params));
      const { data } = await response;
      Modal.success({
        title: "Дата закрытия обновлена",
      });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения даты закрытия цены. Попробуйте позже",
      });
    } finally {
      message.destroy();
    }
  }
);

// Удаление цены для offer-a
export const deleteOfferPrice = createAsyncThunk(
  "offering/deleteOfferPrice",
  async (rateId: number) => {
    try {
      message.loading("Удаление цены");
      const response = axios.delete(deleteOfferPriceRoute(rateId));
      const { data } = await response;
      message.destroy();
      message.success("Цена удалена");
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка удаления цены. Попробуйте позже",
      });
    } finally {
      message.destroy();
    }
  }
);

// Поиск цены для offer-a по id
export const fetchOfferPrice = createAsyncThunk<IOfferPrice, { rateId: string; onDate?: string }>(
  "offering/fetchOfferPrice",
  async (params) => {
    try {
      const response = axios.get<IOfferPrice>(fetchOfferPriceRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка поиска цены. Попробуйте позже",
      });
    } finally {
      message.destroy();
    }
  }
);

// Полчение значений по умолчанию для номенклатуры
export const fetchDefaultServiceNomenclature = createAsyncThunk(
  "service/fetchDefaultServiceNomenclature",
  async (serviceTypeId: number) => {
    try {
      const url = `${baseUrl}/dict-api/properties/service_type/${serviceTypeId}/versions?propertyType=default_charge_name`;
      const { data } = await axios(url);
      return data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Ошибка получения значения по умолчанию Номенклатура для Типа цены", e);
      return [];
    }
  }
);

// // Получение рейтов по списку id
// export const fetchServiceRates = createAsyncThunk<
//   ServiceRate<string>[],
//   { id: number; onDate?: string }[]
// >("offering/fetchServiceRates", async (params) => {
//   try {
//     const response = Promise.all(
//       params.map(({ id, onDate }) =>
//         axios.get<ServiceRate<string>>(fetchOfferPriceRoute({ rateId: id, onDate }))
//       )
//     );
//     const result = (await response).map(({ data }) => data);
//     return result;
//   } catch (e) {
//     return catchApiError({
//       e,
//       title: "Ошибка загрузки цены. Попробуйте позже",
//     });
//   }
// });

// Получение рейтов по списку id из метода получения версий, попадающих в заданный диапазон дат (берется последняя версия)
// Пример использования: получить список рейтов, относящихся к конкретной версии предложения
export const fetchServiceRatesFromVersions = createAsyncThunk<
  ServiceRate<string>[],
  { ids: number[]; fromDate: string; toDate: string }
>("offering/fetchServiceRatesFromVersions", async ({ ids, fromDate, toDate }) => {
  try {
    const { data } = await axios.get<ServiceRate<string>[]>(fetchServiceRatesVersionsRoute(ids));
    data.sort((...args) => sortByToDate(...args, "DESC"));
    const result: ServiceRate<string>[] = [];
    ids.forEach((id) => {
      const datesSatisfyingVersion = data.find((version) => {
        if (version.id === id) {
          // Попадание version.fromDate в заданный диапазон
          const isSatisfyingFromDate = moment(version.fromDate).isBetween(
            fromDate,
            toDate,
            "day",
            "[]"
          );
          // Попадание version.toDate в заданный диапазон
          const isSatisfyingToDate = moment(version.toDate).isBetween(
            fromDate,
            toDate,
            "day",
            "[]"
          );
          // Попадание заданного fromDate в диапазон дат версии
          const isSatisfyingVersionFromDate = moment(fromDate).isBetween(
            version.fromDate,
            version.toDate,
            "day",
            "[]"
          );
          // Попадание заданного toDate в диапазон дат версии
          const isSatisfyingVersionToDate = moment(toDate).isBetween(
            version.fromDate,
            version.toDate,
            "day",
            "[]"
          );
          return (
            isSatisfyingFromDate ||
            isSatisfyingToDate ||
            isSatisfyingVersionFromDate ||
            isSatisfyingVersionToDate
          );
        }
      });
      if (datesSatisfyingVersion) {
        result.push(datesSatisfyingVersion);
      }
    });

    return [...result].sort((a, b) => {
      if (a.id && b.id) {
        return a.id > b.id ? 1 : -1;
      }
      return 0;
    });
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки цен. Попробуйте позже",
    });
  }
});

// Просмотр версий цен
export const fetchServiceRateVersions = createAsyncThunk<ServiceRate<string>[], number>(
  "offering/fetchServiceRateVersions",
  async (id) => {
    try {
      const response = axios.get<ServiceRate<string>[]>(fetchServiceRateVersionsRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий цены. Попробуйте позже",
      });
    }
  }
);

export const offeringSlice = createSlice({
  name: "offering",
  initialState,
  reducers: {
    cleanOfferList: (state) => {
      state.offerListLoading = "idle";
      state.offerListTotalElements = 0;
      state.offerList = [];
    },
    cleanOfferVersions: (state) => {
      state.offerVersionsLoading = "idle";
      state.offerVersions = [];
    },
    cleanOfferVersionsNotForViewing: (state) => {
      state.offerVersionsNotForViewingLoading = "idle";
      state.offerVersionsNotForViewing = [];
    },
    cleanServiceRateVersions: (state) => {
      state.serviceRateVersionsLoading = "idle";
      state.serviceRateVersions = [];
    },
    cleanServiceRates: (state) => {
      state.serviceRatesLoading = "idle";
      state.serviceRates = [];
    },
    cleanServiceProfile: (state) => {
      state.serviceProfileLoading = "idle";
      state.serviceProfile = undefined;
    },
    cleanServiceProfileVersions: (state) => {
      state.serviceProfileVersionsLoading = "idle";
      state.serviceProfileVersions = [];
    },
    cleanOffer: (state) => {
      state.offerLoading = "idle";
      state.offer = undefined;
    },
    cleanOfferPrice: (state) => {
      state.offerPriceLoading = "idle";
      state.offerPrice = undefined;
    },
    cleanOfferHierarchy: (state) => {
      state.offerHierarchyLoading = "idle";
      state.offerHierarchy = undefined;
    },
    cleanOfferHierarchyVersions: (state) => {
      state.offerHierarchyVersionsLoading = "idle";
      state.offerHierarchyVersions = undefined;
    },
    cleanHierarchyOfferList: (state) => {
      state.hierarchyOfferListLoading = "idle";
      state.hierarchyOfferList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfferList.pending, (state) => {
        state.offerListLoading = "pending";
      })
      .addCase(fetchOfferList.rejected, (state) => {
        state.offerListLoading = "rejected";
        state.offerListTotalElements = 0;
        state.offerList = [];
      })
      .addCase(fetchOfferList.fulfilled, (state, { payload }) => {
        state.offerListLoading = "fulfilled";
        state.offerListTotalElements = payload.totalElements;
        state.offerList = payload.content;
      })
      .addCase(fetchServiceTypeList.pending, (state) => {
        state.serviceTypeListLoading = "pending";
      })
      .addCase(fetchServiceTypeList.rejected, (state) => {
        state.serviceTypeListLoading = "rejected";
        state.serviceTypeList = [];
      })
      .addCase(fetchServiceTypeList.fulfilled, (state, { payload }) => {
        state.serviceTypeListLoading = "fulfilled";
        state.serviceTypeList = payload;
      })
      .addCase(createOffer.rejected, (state) => {
        state.offerLoading = "idle"; // Нужно только для флага загрузки, поэтому по rejected в исходное состояние
      })
      .addCase(createOffer.pending, (state) => {
        state.offerLoading = "pending";
      })
      .addCase(updateOfferVersion.pending, (state) => {
        state.offerLoading = "pending";
        // state.offerVersionsLoading = "pending";
      })
      .addCase(updateOfferVersion.rejected, (state) => {
        // Нужно только для флага загрузки, поэтому по rejected в исходное состояние
        state.offerLoading = "idle";
        // state.offerVersionsLoading = "idle";
      })
      .addCase(closeOfferVersion.pending, (state) => {
        state.offerVersionsLoading = "pending";
      })
      .addCase(closeOfferVersion.rejected, (state) => {
        state.offerVersionsLoading = "idle"; // Нужно только для флага загрузки, поэтому по rejected в исходное состояние
      })
      .addCase(fetchOfferById.pending, (state) => {
        state.offerLoading = "pending";
      })
      .addCase(fetchOfferById.rejected, (state) => {
        state.offerLoading = "rejected";
        state.offer = undefined;
      })
      .addCase(fetchOfferById.fulfilled, (state, { payload }) => {
        state.offerLoading = "fulfilled";
        state.offer = payload;
      })
      .addCase(fetchOfferByIdFromVersions.pending, (state) => {
        state.offerLoading = "pending";
      })
      .addCase(fetchOfferByIdFromVersions.rejected, (state) => {
        state.offerLoading = "rejected";
        state.offer = undefined;
      })
      .addCase(fetchOfferByIdFromVersions.fulfilled, (state, { payload }) => {
        state.offerLoading = "fulfilled";
        state.offer = payload;
      })
      .addCase(fetchOfferVersionsById.pending, (state) => {
        state.offerVersionsLoading = "pending";
      })
      .addCase(fetchOfferVersionsById.rejected, (state) => {
        state.offerVersionsLoading = "rejected";
        state.offerVersions = [];
      })
      .addCase(fetchOfferVersionsById.fulfilled, (state, { payload }) => {
        state.offerVersionsLoading = "fulfilled";
        state.offerVersions = [...payload].sort(sortByToDate);
      })
      .addCase(fetchOfferVersionsNotForViewing.pending, (state) => {
        state.offerVersionsNotForViewingLoading = "pending";
      })
      .addCase(fetchOfferVersionsNotForViewing.rejected, (state) => {
        state.offerVersionsNotForViewingLoading = "rejected";
        state.offerVersionsNotForViewing = [];
      })
      .addCase(fetchOfferVersionsNotForViewing.fulfilled, (state, { payload }) => {
        state.offerVersionsNotForViewingLoading = "fulfilled";
        state.offerVersionsNotForViewing = payload;
      })
      .addCase(fetchOfferVersionsByIdWithParams.pending, (state) => {
        state.offerVersionsLoading = "pending";
      })
      .addCase(fetchOfferVersionsByIdWithParams.rejected, (state) => {
        state.offerVersionsLoading = "rejected";
        state.offerVersions = [];
      })
      .addCase(fetchOfferVersionsByIdWithParams.fulfilled, (state, { payload }) => {
        state.offerVersionsLoading = "fulfilled";
        state.offerVersions = [...payload].sort(sortByToDate);
      })
      .addCase(fetchOfferServiceProfileById.pending, (state) => {
        state.serviceProfileLoading = "pending";
      })
      .addCase(fetchOfferServiceProfileById.rejected, (state) => {
        state.serviceProfileLoading = "rejected";
        state.serviceProfile = undefined;
      })
      .addCase(fetchOfferServiceProfileById.fulfilled, (state, { payload }) => {
        state.serviceProfileLoading = "fulfilled";
        state.serviceProfile = payload;
      })
      .addCase(fetchServiceProfileVersions.pending, (state) => {
        state.serviceProfileVersionsLoading = "pending";
      })
      .addCase(fetchServiceProfileVersions.rejected, (state) => {
        state.serviceProfileVersionsLoading = "rejected";
        state.serviceProfileVersions = [];
      })
      .addCase(fetchServiceProfileVersions.fulfilled, (state, { payload }) => {
        state.serviceProfileVersionsLoading = "fulfilled";
        if (Array.isArray(payload)) {
          state.serviceProfileVersions = [...payload].sort(sortByToDate);
        }
      })
      .addCase(updateOfferServiceProfileVersion.pending, (state) => {
        state.serviceProfileLoading = "pending";
        state.serviceProfileVersionsLoading = "pending";
      })
      .addCase(updateOfferServiceProfileVersion.rejected, (state) => {
        // Нужно только для флага загрузки, поэтому по rejected в исходное состояние
        state.serviceProfileLoading = "idle";
        state.serviceProfileVersionsLoading = "idle";
      })
      .addCase(fetchCurrentOfferHierarchy.pending, (state) => {
        state.offerHierarchyLoading = "pending";
      })
      .addCase(fetchCurrentOfferHierarchy.fulfilled, (state, { payload }) => {
        state.offerHierarchyLoading = "fulfilled";
        state.offerHierarchy = payload;
      })
      .addCase(fetchCurrentOfferHierarchy.rejected, (state) => {
        state.offerHierarchyLoading = "idle";
        state.offerHierarchy = undefined;
      })
      .addCase(fetchCurrentOfferHierarchyVersions.pending, (state) => {
        state.offerHierarchyVersionsLoading = "pending";
      })
      .addCase(fetchCurrentOfferHierarchyVersions.fulfilled, (state, { payload }) => {
        state.offerHierarchyVersionsLoading = "fulfilled";
        state.offerHierarchyVersions = payload?.[0];
      })
      .addCase(fetchCurrentOfferHierarchyVersions.rejected, (state) => {
        state.offerHierarchyVersionsLoading = "idle";
        state.offerHierarchyVersions = undefined;
      })
      .addCase(fetchServiceRateVersions.pending, (state) => {
        state.serviceRateVersionsLoading = "pending";
      })
      .addCase(fetchServiceRateVersions.fulfilled, (state, { payload }) => {
        state.serviceRateVersionsLoading = "fulfilled";
        state.serviceRateVersions = [...payload].sort(sortByToDate);
      })
      .addCase(fetchServiceRateVersions.rejected, (state) => {
        state.serviceRateVersionsLoading = "idle";
        state.serviceRateVersions = [];
      })
      // .addCase(fetchServiceRates.pending, (state) => {
      //   state.serviceRatesLoading = "pending";
      // })
      // .addCase(fetchServiceRates.fulfilled, (state, { payload }) => {
      //   state.serviceRatesLoading = "fulfilled";
      //   state.serviceRates = payload;
      // })
      // .addCase(fetchServiceRates.rejected, (state) => {
      //   state.serviceRatesLoading = "idle";
      //   state.serviceRates = [];
      // })
      .addCase(fetchServiceRatesFromVersions.pending, (state) => {
        state.serviceRatesLoading = "pending";
      })
      .addCase(fetchServiceRatesFromVersions.fulfilled, (state, { payload }) => {
        state.serviceRatesLoading = "fulfilled";
        state.serviceRates = payload;
      })
      .addCase(fetchServiceRatesFromVersions.rejected, (state) => {
        state.serviceRatesLoading = "idle";
        state.serviceRates = [];
      })
      .addCase(fetchHierarchyOfferList.pending, (state) => {
        state.hierarchyOfferListLoading = "pending";
      })
      .addCase(fetchHierarchyOfferList.rejected, (state) => {
        state.hierarchyOfferListLoading = "rejected";
        state.hierarchyOfferList = [];
      })
      .addCase(fetchHierarchyOfferList.fulfilled, (state, { payload }) => {
        state.hierarchyOfferListLoading = "fulfilled";
        if (payload.dataForOfferId) {
          state.offerLoading = "fulfilled";
          const findOffer = payload.content.find(
            (offer) => offer.id === payload.dataForOfferId?.selectedOfferId
          );
          if (findOffer) {
            state.offer = findOffer;
          }
        }
        state.hierarchyOfferList = payload.content;
      })
      .addCase(fetchOfferPrice.pending, (state) => {
        state.offerPriceLoading = "pending";
      })
      .addCase(fetchOfferPrice.rejected, (state) => {
        state.offerPriceLoading = "rejected";
        state.hierarchyOfferList = [];
      })
      .addCase(fetchOfferPrice.fulfilled, (state, { payload }) => {
        state.offerPriceLoading = "fulfilled";
        state.offerPrice = payload;
      })
      .addCase(fetchDefaultServiceNomenclature.pending, (state) => {
        state.defaultServiceNomenclatureLoading = "pending";
      })
      .addCase(fetchDefaultServiceNomenclature.rejected, (state) => {
        state.defaultServiceNomenclatureLoading = "rejected";
        state.defaultServiceNomenclature = [];
      })
      .addCase(fetchDefaultServiceNomenclature.fulfilled, (state, { payload }) => {
        const sortedData = payload?.sort((a, b) => a.type.name.localeCompare(b.type.name, "ru"));
        state.defaultServiceNomenclature = sortedData;
        state.defaultServiceNomenclatureLoading = "fulfilled";
      })
      .addCase(editOfferPrice.pending, (state) => {
        state.editOfferPriceLoading = "pending";
      })
      .addCase(editOfferPrice.rejected, (state) => {
        state.editOfferPriceLoading = "rejected";
      })
      .addCase(editOfferPrice.fulfilled, (state, { payload }) => {
        state.editOfferPriceLoading = "fulfilled";
      })
      .addCase(closeOfferPrice.pending, (state) => {
        state.closeOfferPriceLoading = "pending";
      })
      .addCase(closeOfferPrice.rejected, (state) => {
        state.closeOfferPriceLoading = "rejected";
      })
      .addCase(closeOfferPrice.fulfilled, (state, { payload }) => {
        state.closeOfferPriceLoading = "fulfilled";
      })
      .addCase(createOfferPrice.pending, (state) => {
        state.createOfferPriceLoading = "pending";
      })
      .addCase(createOfferPrice.rejected, (state) => {
        state.createOfferPriceLoading = "rejected";
      })
      .addCase(createOfferPrice.fulfilled, (state, { payload }) => {
        state.createOfferPriceLoading = "fulfilled";
      });
  },
});

export const { reducer: offeringReducer, actions: offeringActions } = offeringSlice;
